// copy of packages/integrations/harbor/shared/src/aws-hosting.ts, kept close to
// it so the copies merge into one shared helper (INT-576). Change both or
// neither.

// Harbor's own copy is of github-repositories' rules for where a connector
// runs on AWS, in packages/integrations/github-repositories/shared/src/hosting.ts
// (`awsAccountRules`, `AWS_REGION_PATTERN`, `AWS_OTHER_PARTITION_REGION_PATTERN`
// and `awsRegionRules`). They merge into one in INT-432 and INT-576.

/** An AWS account's ID. A copy of `AWS_ACCOUNT_PATTERN` in
 * `@p0security/integrations-aws-shared/constants`, which github-repositories'
 * rule imports: this package doesn't depend on the AWS integration's, and
 * one pattern isn't worth the dependency. */
const AWS_ACCOUNT_PATTERN = /^\d{12}$/;

/** An AWS region, such as `us-west-2`. */
const AWS_REGION_PATTERN = /^[a-z]+(?:-[a-z]+)+-\d+$/;

/** A region outside AWS's commercial partition: GovCloud, China, the ISO
 * regions (`us-iso-`, `us-isob-`, `eu-isoe-`, `us-isof-`) or the European
 * Sovereign Cloud. P0 invokes the connector, and points it at an instance's
 * secret, by `arn:aws:` ARNs, so the connector can't be in one. */
const AWS_OTHER_PARTITION_REGION_PATTERN =
  /^(?:us-gov-|cn-|[a-z]+-iso[a-z]*-|eusc-)/;

export const INVALID_AWS_ACCOUNT_ID =
  "Enter the 12-digit ID of the AWS account the connector runs in.";

export const INVALID_AWS_REGION =
  "Enter the AWS region the connector runs in, such as us-west-2.";

export const UNSUPPORTED_AWS_REGION =
  "Argo CD supports only AWS commercial regions for now.";

/** Whether `accountId`, ignoring surrounding whitespace, is an AWS account's
 * ID. */
export const isAwsAccountId = (accountId: unknown): accountId is string =>
  typeof accountId === "string" && AWS_ACCOUNT_PATTERN.test(accountId.trim());

/** What to tell the admin when an Argo CD connector can't run in `region`, or
 * `undefined` when it can: {@link INVALID_AWS_REGION} for anything that isn't
 * a region's name, and {@link UNSUPPORTED_AWS_REGION} for a region outside
 * AWS's commercial partition. The value is checked trimmed, as P0 stores it.
 */
export const findAwsRegionProblem = (region: unknown) => {
  if (typeof region !== "string") return INVALID_AWS_REGION;
  const candidate = region.trim();
  if (!AWS_REGION_PATTERN.test(candidate)) return INVALID_AWS_REGION;
  return AWS_OTHER_PARTITION_REGION_PATTERN.test(candidate)
    ? UNSUPPORTED_AWS_REGION
    : undefined;
};
